/**
 * Login screen — email + password against `POST /users/auth/login`.
 *
 * On success: store the user in SessionContext + the react-query cache
 * (so AuthGuard's next read is a cache hit, no `/me` round-trip), then
 * navigate to `/` (or to wherever the AuthGuard bounced us from, if
 * the redirect carried a `from` location).
 *
 * On 401 / network error: surface an animated banner below the form.
 * The banner dismisses on the next submit attempt.
 *
 * Email field validates in real time (debounced 200ms) against a
 * permissive RFC-5321-ish regex. The inline error fades + slides in
 * when the email has content AND fails validation. Reduced-motion
 * users get a cross-fade with no slide.
 *
 * On success the brand red/dark Curtain wipes across the viewport;
 * we navigate during the `covered` phase so the route swap happens
 * behind opaque pixels.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  Curtain,
  CURTAIN_COVER_MS,
  CURTAIN_UNCOVER_MS,
  type CurtainPhase,
} from '@vollgas/shared/components'
import { ButtonSpinner } from '@vollgas/shared/ui'
import { sanitizeEmailInput, isValidEmailFormat } from '@vollgas/shared/utils'
import { useTypeToFocus } from '@vollgas/shared/hooks'
import { Moon, Sun } from '@phosphor-icons/react'
import { ApiError } from '@/apis/apiClient'
import { authApi } from '@/apis/workshopApis'
import type { AuthUser } from '@/apis/types'
import { SESSION_QUERY_KEY } from '@/auth/AuthGuard'
import { landingFor } from '@/auth/landingFor'
import { useSession } from '@/auth/SessionContext'
import { useTheme, Theme } from '@/hooks/useTheme'
import { useI18n } from '@/i18n/TranslationContext'

interface LocationState { readonly from?: string }

const EMAIL_DEBOUNCE_MS = 200

// Password reset lives on the customer-domain web app (the emailed link
// lands there too), so this is a cross-origin link, not a router route.
// `landingFor` with no role is that app's base URL.
const FORGOT_PASSWORD_URL = `${landingFor(null)}/forgot-password`

type BannerKind =
  | 'invalid-credentials'
  | 'network-error'
  | 'oauth-cancelled'
  | 'oauth-failed'
  | 'oauth-unknown-account'

/** Map `?error=` query strings (set by the OAuth callback) to banner kinds. */
const OAUTH_ERROR_MAP: Readonly<Record<string, BannerKind>> = {
  oauth_cancelled: 'oauth-cancelled',
  oauth_failed: 'oauth-failed',
  oauth_unknown_account: 'oauth-unknown-account',
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (typeof window.matchMedia !== 'function') return false
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
  return Boolean(mq?.matches)
}

export function LoginRoute() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const { user, setUser } = useSession()
  const { lang, setLang } = useI18n()
  const { theme, toggle: toggleTheme } = useTheme()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Debounced echo of `email`; only this drives the inline validation
  // so users don't see a red flash on every keystroke.
  const [debouncedEmail, setDebouncedEmail] = useState('')
  const [banner, setBanner] = useState<BannerKind | null>(null)
  const [curtainPhase, setCurtainPhase] = useState<CurtainPhase>('idle')

  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)

  // Type-anywhere-to-resume: a stray keystroke while focus is outside the
  // form refocuses the field the user last touched (email by default).
  // Suspended during the curtain wipe so we don't yank focus mid-exit.
  useTypeToFocus({ refs: [emailRef, passwordRef], disabled: curtainPhase !== 'idle' })

  // Track all in-flight timers so we can clean them up on unmount —
  // and not navigate after the component is gone (StrictMode dev double-mount safety).
  const timersRef = useRef<number[]>([])
  useEffect(() => {
    const timers = timersRef.current
    return () => {
      for (const id of timers) window.clearTimeout(id)
    }
  }, [])

  // Email debounce — string lifecycle:
  //   user types → email changes immediately → after 200ms idle, debouncedEmail catches up.
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedEmail(email), EMAIL_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [email])

  // Surface OAuth callback errors. The Google callback redirects to
  // `/login?error=oauth_cancelled` (user closed Google's consent screen)
  // or `?error=oauth_failed` (any other backend-side OAuth failure).
  // Read once on mount — we don't strip the query string because React
  // Router's MemoryRouter doesn't expose `history.replaceState` easily
  // in tests; a subsequent successful login re-routes anyway.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const oauthError = params.get('error')
    if (oauthError && OAUTH_ERROR_MAP[oauthError]) {
      setBanner(OAUTH_ERROR_MAP[oauthError])
    }
    // Only react to the URL we landed on — subsequent edits to query
    // string by the same form would be a different flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emailInvalid = useMemo(
    () => debouncedEmail.length > 0 && !isValidEmailFormat(debouncedEmail),
    [debouncedEmail],
  )

  // Submit-ability checks the *live* values (not the debounced echo) so a
  // user who types a valid address and hits Enter before the 200ms debounce
  // settles isn't blocked. The form sets `noValidate`, which disables the
  // native `required` enforcement — without this guard an empty email or
  // password would fire a real login request.
  const canSubmit = isValidEmailFormat(email) && password.length > 0

  const login = useMutation<AuthUser, Error>({
    mutationFn: () => authApi.login({ email, password }),
    onSuccess: (data) => {
      setUser(data)
      qc.setQueryData(SESSION_QUERY_KEY, data)
      const target = (location.state as LocationState | null)?.from ?? '/'

      if (prefersReducedMotion()) {
        navigate(target, { replace: true })
        return
      }

      // Curtain wipe: cover → swap route under the slab → uncover.
      setCurtainPhase('covering')
      const t1 = window.setTimeout(() => {
        setCurtainPhase('covered')
        navigate(target, { replace: true })
        // Two rAFs let React commit the navigation behind the curtain
        // before the slab starts retreating.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            setCurtainPhase('uncovering')
            const t2 = window.setTimeout(() => {
              setCurtainPhase('idle')
            }, CURTAIN_UNCOVER_MS + 30)
            timersRef.current.push(t2)
          })
        })
      }, CURTAIN_COVER_MS)
      timersRef.current.push(t1)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        setBanner('invalid-credentials')
      } else {
        // Anything that isn't a 401 from the server — fetch rejection,
        // CORS, DNS, 5xx — surfaces as a network error.
        setBanner('network-error')
      }
    },
  })

  // If the user is already signed in, send them home — covers the case
  // where they manually navigate to /login with a live session.
  if (user) return <Navigate to="/" replace />

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // Guard against empty/invalid input — `noValidate` means the browser
    // won't, so we must. Surface the inline email error immediately
    // instead of waiting on the debounce.
    if (!canSubmit) {
      setDebouncedEmail(email)
      return
    }
    // Dismiss any previous banner before re-submitting (per spec).
    setBanner(null)
    login.mutate()
  }

  const bannerMessage =
    banner === 'invalid-credentials' ? t.login.bannerInvalidCredentials
    : banner === 'network-error'      ? t.login.bannerNetworkError
    : banner === 'oauth-cancelled'    ? t.login.bannerOauthCancelled
    : banner === 'oauth-failed'       ? t.login.bannerOauthFailed
    : banner === 'oauth-unknown-account' ? t.login.bannerOauthUnknownAccount
    : null

  const reduced = prefersReducedMotion()
  const bannerAnimClass = reduced ? 'animate-login-error-fade' : 'animate-login-error-shake'

  // Banner key forces a remount when the banner type changes so the
  // animation re-plays on each new error.
  const bannerKey = `${banner ?? 'none'}-${login.failureCount}`

  return (
    <>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-end p-4">
        <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-glass-border bg-glass-fill-strong p-1 shadow-glass backdrop-blur">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === Theme.Dark ? t.common.switchToLight : t.common.switchToDark}
            title={theme === Theme.Dark ? t.common.switchToLight : t.common.switchToDark}
            className="grid h-8 w-8 place-items-center rounded-full text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
          >
            {theme === Theme.Dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            type="button"
            onClick={() => setLang(lang === 'de' ? 'en' : 'de')}
            aria-label={lang === 'de' ? 'English' : 'Deutsch'}
            title={lang === 'de' ? 'English' : 'Deutsch'}
            className="grid h-8 min-w-[2rem] place-items-center rounded-full px-2 text-11 font-extrabold tracking-mk-up16 text-glass-text-muted hover:bg-glass-fill-strong hover:text-glass-text"
          >
            {lang === 'de' ? 'DE' : 'EN'}
          </button>
        </div>
      </div>
      <main className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col items-stretch justify-center px-6 py-10">
        <div className="mb-6 text-center">
          <img src="/Red-Just-Logo.png" alt="Vollgas" className="mx-auto mb-4 h-[36px] w-auto" width={78} height={36} />
          <h1 className="font-display text-26 font-extrabold tracking-mk-neg5 text-glass-text">
            {t.login.pageTitle}
          </h1>
        </div>

        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-3 rounded-md border border-glass-border bg-glass-fill p-5"
          aria-label={t.login.pageTitle}
          noValidate
        >
          <label className="flex flex-col gap-1 text-13 font-semibold text-glass-text">
            {t.login.emailLabel}
            <input
              ref={emailRef}
              type="email"
              name="email"
              id="login-email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(sanitizeEmailInput(e.target.value))}
              aria-invalid={emailInvalid || undefined}
              aria-describedby={emailInvalid ? 'login-email-error' : undefined}
              className={
                emailInvalid
                  ? 'rounded-sm border border-accent bg-glass-fill px-3 py-2 text-13 font-medium text-glass-text placeholder:text-glass-text-muted focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30'
                  : 'rounded-sm border border-glass-border bg-glass-fill px-3 py-2 text-13 font-medium text-glass-text placeholder:text-glass-text-muted focus:border-glass-border-strong focus:outline-none'
              }
            />
            {emailInvalid && (
              <span
                id="login-email-error"
                role="alert"
                className="mt-1 inline-block text-12-5 font-semibold text-accent animate-field-error-in"
              >
                {t.login.invalidEmailFormat}
              </span>
            )}
          </label>

          <label className="flex flex-col gap-1 text-13 font-semibold text-glass-text">
            {t.login.passwordLabel}
            <input
              ref={passwordRef}
              type="password"
              name="password"
              id="login-password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-sm border border-glass-border bg-glass-fill px-3 py-2 text-13 font-medium text-glass-text placeholder:text-glass-text-muted focus:border-glass-border-strong focus:outline-none"
            />
          </label>

          {/* Outside the <label>: inside it, the link text would become part
              of the password field's accessible name. */}
          <a
            href={FORGOT_PASSWORD_URL}
            className="-mt-1 self-end text-12 font-semibold text-glass-text-muted hover:text-accent"
          >
            {t.login.forgotPassword}
          </a>

          <button
            type="submit"
            // Enabled unless a submit / curtain is in flight — a disabled
            // submit button would swallow the Enter key (implicit submission
            // needs an enabled submit button). onSubmit guards invalid input.
            disabled={login.isPending || curtainPhase !== 'idle'}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-md bg-glass-text px-4 py-3 text-13 font-extrabold uppercase tracking-mk-p4 text-glass-base hover:opacity-90 disabled:opacity-60"
          >
            {login.isPending && <ButtonSpinner />}
            {login.isPending ? t.login.submitting : t.login.submit}
          </button>

          {/* The "or" divider + Google button live INSIDE the form per spec
              (email → password → submit → divider → Google button). The
              Google flow is a full-page navigation, not a form submit, so
              the anchor doesn't interact with the form's submit handler. */}
          <div className="my-2 flex items-center gap-3" role="separator" aria-orientation="horizontal">
            <span className="h-px flex-1 bg-glass-border" />
            <span className="text-11 font-semibold uppercase tracking-mk-up16 text-glass-text-muted">
              {t.login.oauthDivider}
            </span>
            <span className="h-px flex-1 bg-glass-border" />
          </div>

          <a
            href="/users/auth/google/start"
            aria-label={t.login.continueWithGoogle}
            className="inline-flex w-full items-center justify-center gap-3 rounded-md border border-glass-border bg-white px-4 py-3 text-13 font-semibold text-[#1f1f1f] hover:bg-gray-50"
          >
            <GoogleGLogo aria-hidden="true" />
            <span>{t.login.continueWithGoogle}</span>
          </a>

          {bannerMessage && (
            <div
              key={bannerKey}
              role="alert"
              data-banner={banner}
              className={`mt-3 rounded-sm bg-accent/10 px-3 py-2 text-13 font-semibold text-accent ${bannerAnimClass}`}
            >
              {bannerMessage}
            </div>
          )}
        </form>
      </main>
      <Curtain
        phase={curtainPhase}
        leadClassName="bg-accent"
        trailClassName="bg-bg-elevated"
      />
    </>
  )
}

/**
 * Official Google "G" mark. Inline SVG per design system (no icon dep
 * for a single mark). Colours come straight from Google's Identity
 * brand guidelines (https://developers.google.com/identity/branding-guidelines).
 */
function GoogleGLogo({ 'aria-hidden': ariaHidden }: { 'aria-hidden'?: boolean | 'true' | 'false' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 48 48"
      aria-hidden={ariaHidden}
    >
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  )
}
